import { Component, ElementRef, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CriteriaElement } from '../global-criteria-list';
import { InputSearchCriteria } from 'app/dashboard/leads/visits-request';
import { SiteSpecific } from 'app/core/site-specific.base';
import { LoggerFactory } from 'app/core/logger.service';
import { UserSession } from 'app/core/user-session';
import { VisitsService } from 'app/visits/visits.service';
import { KeyValuePair } from 'app/shared/key-value-pair';
import { LoadSuggestionsService } from '../load-suggestions.service';
import { FilterProperty } from '../filter-property';
import { SelectionsService } from '../../shared/services/selections.service';
import { TranslateService } from '@ngx-translate/core';
import { Widget, WidgetSimpleInfo } from '../../campaign/widgets.model';
import WidgetComponentHelpers from '../../campaign/widget-component-helpers';
import { Account } from 'app/back-office/account.model';
import { WidgetService } from '../../shared/services/widget.service';
import { CampaignStatus, OnsiteCampaignType } from '../../campaign/campaign.model';
import { LanguageService } from '../../core/language.service';
import { ValidationService } from '../../shared/services/validation.service';

@Component({
  selector: 'app-criteria-details',
  templateUrl: './criteria-details.component.html',
  styleUrls: ['./criteria-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CriteriaDetailsComponent extends SiteSpecific {
  private triggerElementRef: ElementRef;
  public inputSearchCriteria: InputSearchCriteria;
  public header: string;
  public codeEditorHeight: number;
  public description: string;
  public operators: KeyValuePair<number>[];
  public inputType: string;
  public suggestions: FilterProperty[];
  public simpleDataForCriteria: string[] = [];
  public selectedInput = '';
  public loading: boolean;
  public showOperator: boolean;
  public popularSuggestions: any[];
  public hasPopular: boolean;
  public selectedTabIndex: number;
  public widgetsSimpleInfo: Array<WidgetSimpleInfo>;
  public widgets: Array<WidgetSimpleInfo>;
  public activeWidgets: Array<WidgetSimpleInfo>;
  public inactiveWidgets: Array<WidgetSimpleInfo>;
  public chosenActiveWidgetIds: Array<number>;
  public chosenInactiveWidgetIds: Array<number>;
  public widgetsForSelector: WidgetSimpleInfo[] = [];
  public selectedWidgetIds: number[] = [];
  public consentOptions = [
    { value: 0, text: 'hasConsent', exp: 'Consent = any' },
    { value: 1, text: 'hasSpecificConsent' },
    { value: 2, text: 'hasNotConsent', exp: 'Consent != any' },
  ];
  public consentModel = this.consentOptions[0];
  public isInputValid = true;
  public update: boolean;

  private oldOperator: number;
  private operatorAnyValue = 12;
  private inputsWithSupportForAny = ['goals', 'campaigns'];
  protected isAnyChecked = false;
  public isOnsiteCampaignDialog = false;
  public isCountryFilter = false;

  constructor(
    public dialogRef: MatDialogRef<CriteriaDetailsComponent>,
    protected readonly userSession: UserSession,
    protected readonly loggerFactory: LoggerFactory,
    private readonly loadSuggestionsService: LoadSuggestionsService,
    private readonly visitsService: VisitsService,
    private readonly validationService: ValidationService,
    private languageService: LanguageService,
    private widgetService: WidgetService,
    private selectionsService: SelectionsService,
    private translationService: TranslateService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      currentAccount: Account;
      trigger: ElementRef;
      option: CriteriaElement;
      input?: InputSearchCriteria;
      update: boolean;
      simpleDataForCriteria?: any;
      widgets: WidgetSimpleInfo[];
    },
  ) {
    super(userSession, loggerFactory);
    this.triggerElementRef = data.trigger;
    this.header = data.option.name;
    this.codeEditorHeight = this.header === 'jsExpression' ? 100 : 150;
    this.inputType = data.option.formFieldType;
    this.isOnsiteCampaignDialog = data.option.name.startsWith('widget-');

    this.inputSearchCriteria = new InputSearchCriteria();
    this.showOperator = true;
    this.simpleDataForCriteria = data.simpleDataForCriteria || [];
    this.widgets = data.widgets || [];
  }

  public getTitle(header): string {
    switch (header) {
      case 'jsExpression':
      case 'cookieExists':
      case 'cookieEquals':
      case 'domain':
      case 'groupName':
      case 'csat-score':
      case 'nps-score':
        return '';
    }
    return 'generic.all';
  }

  protected async onSiteChanged() {
    this.loading = true;

    this.inputSearchCriteria.type = this.header;

    this.update = this.data.update;
    if (this.data.input) {
      let input = this.data.input.input as any;
      if ((this.data.input as any).defaultValue) {
        input = (this.data.input as any).defaultValue;
      }
      if (this.data.option.formFieldType === 'MultiDropdown') {
        this.inputSearchCriteria.operator = this.data.input.operator;
        this.inputSearchCriteria.input = input instanceof Array ? input : input.split(',');
      } else if (this.data.option.formFieldType === 'AutoCompleteField') {
        if (this.data.input.operator) {
          this.inputSearchCriteria.operator = +WidgetComponentHelpers.ugliffyOperator(
            this.data.input.operator.toString(),
          );
        }
        if (this.inputSearchCriteria.operator === 0 || this.inputSearchCriteria.operator === 1) {
          this.inputSearchCriteria.input = input instanceof Array ? input : input.split(',');
        } else {
          this.inputSearchCriteria.input = input;
        }
      } else {
        if (this.inputSearchCriteria.operator) {
          this.inputSearchCriteria.operator = +WidgetComponentHelpers.ugliffyOperator(
            this.data.input.operator.toString(),
          );
        }
        if (!input) {
          this.inputSearchCriteria.input = (this.data.input as any).defaultValue;
        } else {
          this.inputSearchCriteria.input = input;
          if (this.data.input.operator) {
            this.inputSearchCriteria.operator = this.data.input.operator;
          }
        }
      }
      this.consentModel = this.consentOptions.find((option) => option.exp === this.data.input.displayExp);
      if (!this.consentModel) {
        this.consentModel = this.consentOptions[1];
      }
    } else {
      this.consentModel = this.consentOptions[0];
    }

    this.setDescription();
    await this.fetchOperators();
    await this.loadInputSuggestions();

    this.loadSuggestionsService.clearStaticCaches();
    this.inputSearchCriteria.type = this.header;

    this.isAnyChecked = this.inputSearchCriteria.operator === this.operatorAnyValue;
    await this.loadPopularInputSuggestions();

    this.loading = false;
  }

  private setDescription() {
    switch (this.inputSearchCriteria.type) {
      case 'csat-score':
        this.description = 'visits.criteriaProperties.csat-score-description';
        break;
      case 'nps-score':
        this.description = 'visits.criteriaProperties.nps-score-description';
        break;
      default:
        this.description = undefined;
    }
  }

  public getDynamicWidgetName(widget: WidgetSimpleInfo | Widget): string {
    let campaignName: string;
    let isABWidget = widget.campaignType !== OnsiteCampaignType.ABTesting;
    if ('campaign' in widget) {
      campaignName = widget.campaign.name || this.translationService.instant('campaign.unnamed');
      isABWidget = widget.campaign.type === OnsiteCampaignType.ABTesting;
    } else {
      campaignName = widget.campaignName || this.translationService.instant('campaign.unnamed');
      isABWidget = widget.campaignType === OnsiteCampaignType.ABTesting;
    }
    if (widget.name) {
      return campaignName + ' (' + widget.name + ')';
    } else if (!isABWidget) {
      return this.languageService.translateInstantly('campaign.settings.conditions.gen-campaign-name-variation', {
        campaignName: campaignName,
        variantId: widget.order + 1,
      });
    } else {
      const controlOrChallenger =
        widget.order === 0
          ? this.languageService.translateInstantly('campaign.ab-test.control')
          : this.languageService.translateInstantly('campaign.ab-test.challenger');
      return this.languageService.translateInstantly('campaign.settings.conditions.gen-campaign-name-ab-test', {
        campaignName: campaignName,
        abTestVariant: controlOrChallenger,
      });
    }
  }

  public getCleanedHtml(html: string) {
    return html.replace(/<.+?>/, '').replace(/<\/.+?>/, '');
  }

  async fetchOperators() {
    if (this.inputType.toLowerCase().indexOf('custom') !== -1) {
      this.showOperator = false;
    }
    if (this.inputType.toLowerCase().indexOf('radio') !== -1) {
      this.showOperator = false;
    }
    if (this.data.option.formFieldType === 'NumberField') {
      this.operators = this.visitsService.getNumberOperators();
    } else {
      this.operators = this.visitsService.getStringOperators();
      if (this.inputsWithSupportForAny.indexOf(this.inputSearchCriteria.type) === -1) {
        this.operators = this.operators.filter((x) => x.value !== this.operatorAnyValue);
      }
    }

    if (!this.inputSearchCriteria.operator) {
      // default value
      if (this.inputSearchCriteria.type === 'referrerURL') {
        this.inputSearchCriteria.operator = this.operators[4].value;
      } else if (this.inputSearchCriteria.type === 'goals') {
        if (!this.inputSearchCriteria.operator && !this.inputSearchCriteria.input) {
          this.inputSearchCriteria.operator = this.operators.find((o) => o.key === 'Any').value;
          this.inputSearchCriteria.input = ['Any'];
        }
      } else {
        this.inputSearchCriteria.operator = this.operators[0].value;
      }
      if (this.inputSearchCriteria.type === 'siteTime' || this.inputSearchCriteria.type === 'pageViews') {
        this.inputSearchCriteria.operator = this.operators.filter(
          (x) => x.key.toLowerCase().indexOf('morethan') >= 0,
        )[0].value; // More than Operator
      }
    }
    if (this.inputSearchCriteria.type === 'cookieExists') {
      this.operators = this.operators.filter(
        (k) => k.key.toLowerCase() === 'equals' || k.key.toLowerCase() === 'contains',
      );
    }
    if (
      this.inputSearchCriteria.type === 'cookieEquals' ||
      this.inputSearchCriteria.type === 'jsExpression' ||
      this.inputSearchCriteria.type === 'groupName'
    ) {
      this.operators = [];
    }
    if (this.data.option.formFieldType === 'MultiDropdown') {
      // remove contains, starts with when showing a drop down
      this.operators = this.operators.filter((k) => k.key.toLowerCase().indexOf('equals') !== -1);
    }
    if (this.inputSearchCriteria.type === 'domain') {
      this.operators = this.operators.filter(
        (k) => k.key.toLowerCase() === 'equals' || k.key.toLowerCase() === 'notequals',
      );
    }
    // exclusion properties must have equals operator by default
    if (this.data.option.name === 'notCampaigns' || this.data.option.name === 'notGoals') {
      this.showOperator = false;
      this.inputSearchCriteria.operator = this.operators.filter(
        (x) => x.key.toLowerCase().indexOf('equals') === 0,
      )[0].value;
    }

    // Filter profilings operators
    if (this.data.option.name === 'profilings') {
      this.operators = this.operators.filter(
        (k) => k.key.toLowerCase().indexOf('equals') > -1 || k.key.toLowerCase().indexOf('notequals') > -1,
      );
    }
  }

  public getUserHint(inputSearchCriteria): string {
    switch (inputSearchCriteria.type) {
      case 'cookieExists':
        return 'campaign.settings.conditions.cookie-exist-hint';
      case 'cookieEquals':
        return 'campaign.settings.conditions.cookie-value-hint';
      case 'jsExpression':
        return 'campaign.settings.conditions.js-expression-hint';
      case 'domain':
        return this.languageService.translateInstantly('campaign.settings.conditions.site-domain-hint', {
          primaryDomain: this.data.currentAccount.urls[0],
        });
      default:
        return '';
    }
  }

  close(): void {
    this.dialogRef.close(this.inputSearchCriteria);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  updateDialogPosition() {
    const matDialogConfig: MatDialogConfig = new MatDialogConfig();
    const rect = this.triggerElementRef.nativeElement.getBoundingClientRect();
    matDialogConfig.position = {
      left: `${rect.left}px`,
      top: `${rect.bottom - 50}px`,
    };
    this.dialogRef.updatePosition(matDialogConfig.position);
  }

  // Popular goals came from goal/top, which read the removed Analytics service (#19808), so there are none.
  async loadPopularInputSuggestions() {
    this.popularSuggestions = [];
    if (this.inputSearchCriteria.type === 'goals') {
      this.widgetsSimpleInfo = await this.selectionsService.getAllWidgetsSimpleInfosAsync();
    }
  }

  onPopularClicked(event: MatCheckboxChange, id: string) {
    let input = this.inputSearchCriteria.input as string[];
    if (!input || (input.length === 1 && input[0] === 'Any')) {
      input = [];
    }
    input = input.slice(0); // Create copy so that our push/pop actually updates
    if (event.checked) {
      this.inputSearchCriteria.operator = undefined;
      this.isAnyChecked = false;
      if (input.indexOf(id) === -1) {
        input.push(id);
      }
    } else {
      input = input.filter((x) => x !== id);
    }
    this.inputSearchCriteria.input = input;
  }

  showAny() {
    // Show only if we have an 'Any' operator
    return this.inputsWithSupportForAny.indexOf(this.inputSearchCriteria.type) !== -1;
  }

  onAnyClicked(event: MatCheckboxChange) {
    if (event.checked) {
      this.oldOperator = this.inputSearchCriteria.operator;
      this.inputSearchCriteria.operator = this.operatorAnyValue;
      this.inputSearchCriteria.input = ['Any'];
      this.uncheckInputs();
      this.isAnyChecked = true;
    } else {
      this.inputSearchCriteria.operator = this.oldOperator;
      this.inputSearchCriteria.input = undefined;
    }
  }

  private uncheckInputs() {
    this.popularSuggestions.forEach((item) => {
      item.checked = false;
    });
  }

  submitIsDisabled() {
    if (this.header === 'consent') {
      if (this.consentModel.value === 1) {
        this.inputSearchCriteria.operator = 0;
        return (
          !this.inputSearchCriteria.input ||
          this.inputSearchCriteria.input[0] === 'any' ||
          this.inputSearchCriteria.input.length === 0
        );
      } else {
        this.inputSearchCriteria.operator = this.consentModel.value === 0 ? 0 : 1;
        this.inputSearchCriteria['input'] = ['any'];
        return false;
      }
    } else {
      if (this.inputSearchCriteria.operator === this.operatorAnyValue) {
        return false;
      }
      if (this.inputSearchCriteria.input) {
        if (typeof this.inputSearchCriteria.input === 'object') {
          return !this.inputSearchCriteria.input.length;
        } else {
          return !this.isInputValid;
        }
      } else if (typeof this.inputSearchCriteria.input === 'boolean') {
        return false;
      }
      return true;
    }
  }

  public validateInput() {
    if (this.inputSearchCriteria.type === 'cookieEquals') {
      const regex = /^[a-zA-Z0-9_\.-]*[=][a-zA-Z0-9_\.-]+$/g;
      this.isInputValid = regex.test(this.inputSearchCriteria.input);
    } else if (this.inputSearchCriteria.type === 'domain') {
      this.isInputValid = this.validationService.validateDomainName(this.inputSearchCriteria.input, true);
    }
  }

  public stripProtocol(input: string) {
    const noProtocol = input.replace(/^\/\/|^.*?:(\/\/)?/, '');
    this.inputSearchCriteria.input = noProtocol.split('/')[0];
  }

  public getTextboxPlaceholderTranslationKey(): string {
    if (this.inputSearchCriteria.type === 'cookieEquals') {
      return 'visits.criteriaProperties.cookieEqualsPlaceHolder';
    }
    return 'generic.typeSomething';
  }

  showInputFields() {
    return this.inputSearchCriteria.operator !== this.operatorAnyValue;
  }

  async loadInputSuggestions() {
    let response = [];
    if (this.inputSearchCriteria.type === 'goals' || this.inputSearchCriteria.type === 'notGoals') {
      response = await this.loadSuggestionsService.getGoalsAsync();
    } else if (this.inputSearchCriteria.type === 'country') {
      this.isCountryFilter = true;
      return;
    } else if (this.inputSearchCriteria.type.trim() === 'browserLanguage') {
      response = WidgetComponentHelpers.getLanguagesForAudience();
    } else if (
      this.inputSearchCriteria.type === 'campaigns' ||
      this.inputSearchCriteria.type === 'utmCampaign' ||
      this.inputSearchCriteria.type === 'notCampaigns'
    ) {
      response = await this.loadSuggestionsService.getCampaignsAsync();
    } else if (this.inputSearchCriteria.type === 'labels') {
      response = await this.loadSuggestionsService.getLabelsAsync();
    } else if (this.inputSearchCriteria.type === 'profilings') {
      response = await this.loadSuggestionsService.getProfilingTopicsAsync();
    } else if (this.inputSearchCriteria.type === 'visitedPages') {
      response = await this.loadSuggestionsService.getVisitedPages();
    } else if (this.inputSearchCriteria.type === 'identified') {
      // TODO: load from service
      response = [new FilterProperty().set(true, 'Identified'), new FilterProperty().set(false, 'Not Identified')];
    } else if (this.inputSearchCriteria.type === 'emailCampaigns') {
      response = [new FilterProperty().set('1', 'Yes'), new FilterProperty().set('0', 'No')];
    } else if (this.inputSearchCriteria.type === 'consent') {
      response = await this.selectionsService.getVersions();
    } else if (this.inputSearchCriteria.type === 'accepted') {
      response = await this.selectionsService.getVersions();
    } else if (this.inputSearchCriteria.type.startsWith('widget-') && this.widgets.length) {
      response = await this.loadSuggestionsService.getWidgetsAsync();
      // this.widgets is already simple-widgets data; just type-filter it for the selector.
      this.widgetsForSelector = this.filterTypeCampaigns(this.widgets ?? []);
      this.selectedWidgetIds = this.inputSearchCriteria.input
        ? this.inputSearchCriteria.input.map((id) => Number(id))
        : [];
    }
    if (response) {
      this.suggestions = response;
    }
  }

  private filterTypeCampaigns(allWidgets: WidgetSimpleInfo[]): WidgetSimpleInfo[] {
    if (this.inputSearchCriteria.type === 'widget-clickthroughs') {
      return allWidgets.filter((widget) => {
        return (
          (widget.campaignType === OnsiteCampaignType.Regular && !widget.hasInputFields) ||
          (widget.campaignType === OnsiteCampaignType.ABTesting && !widget.hasInputFields)
        ); //promotion
      });
    } else if (this.inputSearchCriteria.type === 'widget-conversions') {
      return allWidgets.filter((widget) => {
        return (
          (widget.campaignType === OnsiteCampaignType.Regular && widget.hasInputFields) ||
          (widget.campaignType === OnsiteCampaignType.ABTesting && widget.hasInputFields)
        ); //form
      });
    } else if (this.inputSearchCriteria.type === 'widget-survey-response') {
      return allWidgets.filter((widget) => {
        return widget.campaignType === OnsiteCampaignType.Survey; //survey
      });
    } else {
      return allWidgets;
    }
  }

  public onWidgetSelectionChange(ids: number[]): void {
    this.inputSearchCriteria.input = ids.length ? ids.map((id) => String(id)) : undefined;
  }

  addSelectedInput(value: string | any) {
    this.inputSearchCriteria.input = value.value || value;
  }

  addCriteria($event) {
    this.inputSearchCriteria = $event;
  }

  operatorChanged(event) {
    if (event === 0 || event === 1) {
      if (!Array.isArray(this.inputSearchCriteria.input)) {
        this.inputSearchCriteria.input = [];
      }
    }
    this.inputSearchCriteria.operator = event;
  }
}
